import { lazy } from 'react';

// Issue #1068 — recover from ChunkLoadError instead of dead-ending in
// AppErrorBoundary.
//
// A lazy route's dynamic import() can fail when the hashed chunk it asks for is
// missing (a tab holding a pre-deploy index.html requests a chunk hash that no
// longer exists) or is briefly 404/HTML during a deploy. Left unhandled this
// throws to AppErrorBoundary and the page dies until a manual reload.
//
// Recovery is two-staged:
//   1. Retry the import a couple of times with a short delay. This covers the
//      transient mid-deploy window where the build dir is momentarily wiped —
//      once serve is back (and, post the nginx fix in this issue, the bad
//      response is `no-store` rather than a cached immutable 404) the retry
//      fetches the real asset.
//   2. If retries are exhausted, do a single full reload. Reloading re-fetches
//      index.html (served `no-cache`), which references the *new* chunk hashes,
//      so the stale-hash case resolves. A time-based sessionStorage guard makes
//      a reload loop impossible: we only reload if we haven't already reloaded
//      in the last RELOAD_GUARD_MS.
//
// Note: the original issue suggested retrying with a cache-busting query param.
// webpack owns the chunk URLs for a static import(), so a per-chunk query param
// isn't reliably achievable; the retry + guarded reload above is equivalent
// because the nginx change makes any poisoned entry uncacheable and the reload
// picks up fresh hashes.

const RELOAD_KEY = 'chunk-reload-at';
const RELOAD_GUARD_MS = 10000;

export function isChunkLoadError(error) {
  if (!error) return false;
  const name = error.name || '';
  const message = error.message || '';
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\w./-]+ failed/i.test(message) ||
    /Loading CSS chunk [\w./-]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    // MIME mismatch produced when a missing /static asset is answered with the
    // SPA fallback (text/html) — "...because its MIME type ('text/html')...".
    /MIME type \('text\/html'\)/i.test(message)
  );
}

export function retryImport(importer, retries = 2, delayMs = 300) {
  return importer().catch((error) => {
    if (retries <= 0) throw error;
    return new Promise((resolve) => setTimeout(resolve, delayMs)).then(() =>
      retryImport(importer, retries - 1, delayMs)
    );
  });
}

// Returns true if a reload was triggered (caller should stall), false if the
// guard blocked it (caller should surface the error).
export function reloadOnceForChunkError() {
  let last = 0;
  try {
    last = Number(window.sessionStorage.getItem(RELOAD_KEY)) || 0;
  } catch (e) {
    // sessionStorage unavailable (private mode / disabled) — fall through and
    // reload without a guard rather than losing recovery entirely.
  }
  if (Date.now() - last < RELOAD_GUARD_MS) {
    return false;
  }
  try {
    window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch (e) {
    /* ignore */
  }
  window.location.reload();
  return true;
}

// Drop-in replacement for React.lazy for lazy-loaded routes. `chunkName` is
// optional and only used for diagnostics.
export function lazyWithRetry(importer, chunkName) {
  return lazy(() =>
    retryImport(importer).catch((error) => {
      if (!isChunkLoadError(error)) throw error;
      // eslint-disable-next-line no-console
      console.warn(
        `[lazyWithRetry] chunk load failed${chunkName ? ` (${chunkName})` : ''}, reloading`,
        error
      );
      if (reloadOnceForChunkError()) {
        // Never resolve: keep the Suspense fallback on screen while the browser
        // navigates away, instead of flashing the error boundary.
        return new Promise(() => {});
      }
      throw error;
    })
  );
}

export default lazyWithRetry;
